import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { ICard } from 'src/app/models/card';
import { ICardCollection } from 'src/app/models/collection';
import { CardsService } from './cards.service';
import { FirebaseAuthService } from './firebase-auth.service';

/**
 * Mode for data refresh operations
 */
export enum RefreshMode {
  /** Only update views count - minimal impact, no reordering */
  VIEWS_ONLY = 'views-only',
  /** Update views and other safe fields (future use) */
  PARTIAL = 'partial',
  /** Full data reload - may cause reordering */
  FULL = 'full'
}

/**
 * Represents a views update for a single item
 */
export interface ViewsUpdate {
  id: string;
  oldViews: number;
  newViews: number;
}

/**
 * Result of a views refresh operation
 */
export interface ViewsRefreshResult {
  modelsUpdated: ViewsUpdate[];
  collectionsUpdated: ViewsUpdate[];
  timestamp: Date;
}

/**
 * Service responsible for refreshing views data on app activation.
 * Designed to update only specific fields (like views) without
 * affecting the order or other properties of existing data.
 *
 * Architecture allows for future expansion to partial or full updates.
 */
@Injectable({
  providedIn: 'root'
})
export class ViewsUpdateService implements OnDestroy {

  /** Minimum interval between views updates (in milliseconds) */
  private readonly MIN_UPDATE_INTERVAL_MS = 30 * 1000; // 30 seconds

  /** Timestamp of last successful update */
  private lastUpdateTime: Date | null = null;

  /** Flag to prevent concurrent updates */
  private isUpdateInProgress = false;

  /** Flag to track if service has been destroyed */
  private isDestroyed = false;

  /** Subject to notify about views changes */
  public onViewsUpdated: Subject<ViewsRefreshResult> = new Subject();

  constructor(
    private cardsService: CardsService,
    private firebaseAuth: FirebaseAuthService
  ) {}

  /**
   * Main entry point for refreshing data on app activation.
   * By default only refreshes views to prevent UI disruption.
   *
   * @param mode - The refresh mode to use
   * @param uploadedCards - Reference to uploaded cards array from UserDataService
   * @param draftCollections - Reference to draft collections array from UserDataService
   * @returns Promise with refresh result or null if skipped
   */
  async refreshOnAppActivation(
    mode: RefreshMode = RefreshMode.VIEWS_ONLY,
    uploadedCards: ICard[],
    draftCollections: ICardCollection[]
  ): Promise<ViewsRefreshResult | null> {

    // Guard: check if service is destroyed
    if (this.isDestroyed) {
      console.log('[ViewsUpdateService] Skipping update - service is destroyed');
      return null;
    }

    // Guard: check if update should be skipped
    if (this.shouldSkipUpdate()) {
      console.log('[ViewsUpdateService] Skipping update - too soon or already in progress');
      return null;
    }

    // Guard: check if user is authenticated
    const uid = await this.firebaseAuth.getUserUid();
    if (!uid || this.isDestroyed) {
      console.log('[ViewsUpdateService] Skipping update - user not authenticated or service destroyed');
      return null;
    }

    // Guard: check if there's data to update
    if (uploadedCards.length === 0 && draftCollections.length === 0) {
      console.log('[ViewsUpdateService] Skipping update - no data to refresh');
      return null;
    }

    this.isUpdateInProgress = true;

    try {
      switch (mode) {
        case RefreshMode.VIEWS_ONLY:
          return await this.refreshViewsOnly(uid, uploadedCards, draftCollections);

        case RefreshMode.PARTIAL:
          // Future: implement partial update (views + selected fields)
          console.warn('[ViewsUpdateService] PARTIAL mode not yet implemented, falling back to VIEWS_ONLY');
          return await this.refreshViewsOnly(uid, uploadedCards, draftCollections);

        case RefreshMode.FULL:
          // Future: implement full reload through UserDataService
          console.warn('[ViewsUpdateService] FULL mode not yet implemented, falling back to VIEWS_ONLY');
          return await this.refreshViewsOnly(uid, uploadedCards, draftCollections);

        default:
          return await this.refreshViewsOnly(uid, uploadedCards, draftCollections);
      }
    } catch (error) {
      console.error('[ViewsUpdateService] Error during refresh:', error);
      return null;
    } finally {
      this.isUpdateInProgress = false;
    }
  }

  /**
   * Refreshes only the views property of models and collections.
   * Does NOT affect order or other properties.
   */
  private async refreshViewsOnly(
    uid: string,
    uploadedCards: ICard[],
    draftCollections: ICardCollection[]
  ): Promise<ViewsRefreshResult> {

    console.log('[ViewsUpdateService] Starting views-only refresh...');

    // Fetch fresh data from backend in parallel
    const [freshModels, freshCollections] = await Promise.all([
      this.cardsService.getUserUploadedModels(uid).catch(err => {
        console.error('[ViewsUpdateService] Failed to fetch models:', err);
        return [] as ICard[];
      }),
      this.cardsService.getUserDraftCollections().catch(err => {
        console.error('[ViewsUpdateService] Failed to fetch collections:', err);
        return [] as ICardCollection[];
      })
    ]);

    // Check if service was destroyed during async operations
    if (this.isDestroyed) {
      console.log('[ViewsUpdateService] Service destroyed during fetch, aborting');
      return {
        modelsUpdated: [],
        collectionsUpdated: [],
        timestamp: new Date()
      };
    }

    // Merge views into existing data
    const modelsUpdated = this.mergeViewsIntoModels(uploadedCards, freshModels);
    const collectionsUpdated = this.mergeViewsIntoCollections(draftCollections, freshCollections);

    // Update timestamp
    this.lastUpdateTime = new Date();

    const result: ViewsRefreshResult = {
      modelsUpdated,
      collectionsUpdated,
      timestamp: this.lastUpdateTime
    };

    // Log results
    if (modelsUpdated.length > 0 || collectionsUpdated.length > 0) {
      console.log('[ViewsUpdateService] Views updated:', {
        models: modelsUpdated.length,
        collections: collectionsUpdated.length,
        details: { modelsUpdated, collectionsUpdated }
      });
    } else {
      console.log('[ViewsUpdateService] No views changes detected');
    }

    // Notify subscribers only if service is not destroyed
    if (!this.isDestroyed) {
      this.onViewsUpdated.next(result);
    }

    return result;
  }

  /**
   * Merges views from fresh models into existing models array.
   * Only updates the views property, preserving all other data.
   */
  private mergeViewsIntoModels(existingModels: ICard[], freshModels: ICard[]): ViewsUpdate[] {
    const updates: ViewsUpdate[] = [];

    // Create a map for O(1) lookup
    const freshModelsMap = new Map<string, ICard>();
    freshModels.forEach(model => freshModelsMap.set(model.id, model));

    // Update views in existing models
    existingModels.forEach(existingModel => {
      const freshModel = freshModelsMap.get(existingModel.id);

      if (freshModel && existingModel.views !== freshModel.views) {
        const oldViews = existingModel.views || 0;
        const newViews = freshModel.views || 0;

        // Only update if there's an actual change
        if (oldViews !== newViews) {
          existingModel.views = newViews;
          updates.push({
            id: existingModel.id,
            oldViews,
            newViews
          });
        }
      }
    });

    return updates;
  }

  /**
   * Merges views from fresh collections into existing collections array.
   * Updates views for each model within collections.
   */
  private mergeViewsIntoCollections(
    existingCollections: ICardCollection[],
    freshCollections: ICardCollection[]
  ): ViewsUpdate[] {
    const updates: ViewsUpdate[] = [];

    // Create a map for O(1) collection lookup
    const freshCollectionsMap = new Map<string, ICardCollection>();
    freshCollections.forEach(col => freshCollectionsMap.set(col.collectionId, col));

    existingCollections.forEach(existingCollection => {
      const freshCollection = freshCollectionsMap.get(existingCollection.collectionId);

      if (freshCollection && freshCollection.collectionCards) {
        // Create a map for O(1) card lookup within this collection
        const freshCardsMap = new Map<string, ICard>();
        freshCollection.collectionCards.forEach(card => freshCardsMap.set(card.id, card));

        // Update views for each card in the collection
        existingCollection.collectionCards?.forEach(existingCard => {
          const freshCard = freshCardsMap.get(existingCard.id);

          if (freshCard && existingCard.views !== freshCard.views) {
            const oldViews = existingCard.views || 0;
            const newViews = freshCard.views || 0;

            if (oldViews !== newViews) {
              existingCard.views = newViews;
              updates.push({
                id: `${existingCollection.collectionId}/${existingCard.id}`,
                oldViews,
                newViews
              });
            }
          }
        });
      }
    });

    return updates;
  }

  /**
   * Determines if the update should be skipped based on timing and state.
   */
  private shouldSkipUpdate(): boolean {
    // Skip if update is already in progress
    if (this.isUpdateInProgress) {
      return true;
    }

    // Skip if last update was too recent
    if (this.lastUpdateTime) {
      const timeSinceLastUpdate = Date.now() - this.lastUpdateTime.getTime();
      if (timeSinceLastUpdate < this.MIN_UPDATE_INTERVAL_MS) {
        return true;
      }
    }

    return false;
  }

  /**
   * Forces an immediate refresh, bypassing the time check.
   * Use sparingly - mainly for pull-to-refresh scenarios.
   */
  async forceRefresh(
    mode: RefreshMode,
    uploadedCards: ICard[],
    draftCollections: ICardCollection[]
  ): Promise<ViewsRefreshResult | null> {
    // Reset last update time to bypass the check
    this.lastUpdateTime = null;
    return this.refreshOnAppActivation(mode, uploadedCards, draftCollections);
  }

  /**
   * Resets the service state. Useful for logout scenarios.
   */
  reset(): void {
    this.lastUpdateTime = null;
    this.isUpdateInProgress = false;
  }

  /**
   * Cleanup when service is destroyed
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.isUpdateInProgress = false;
    this.onViewsUpdated.complete();
  }
}
